/**
 * userStore — Supabase session + profile + effective tier (ADR-0021).
 *
 * Three mental models in one store:
 *
 *   - status: 'loading' | 'anon' | 'signed-in'
 *       'loading' is the bootstrap window (mount → first /api/auth/me).
 *       After that we're either anon or signed-in.
 *
 *   - profile: { tier, role, displayName, ... } | null
 *       Populated when signed in AND `/api/auth/me` returned a row.
 *
 *   - effectiveTier: 'anonymous' | 'free' | 'pro' | 'max'
 *       The single gate predicate — admins read as 'max', anonymous as
 *       'anonymous'. Mirrors `getEffectiveTier()` on the server.
 *
 * Persistence: only the cached profile is persisted. The session itself
 * lives in localStorage under 'jubuddy:supabase-auth' (Supabase default)
 * — we do NOT duplicate it here.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  coerceRole,
  coerceTier,
  getEffectiveTier,
  type Role,
  type Tier,
} from '../auth/effective-tier.ts';
import { isAuthConfigured, supabase } from '../lib/supabase.ts';
import {
  JubitSsoBridgeError,
  openJubitSsoBridge,
  shouldUseSsoBridge,
} from '../auth/jubitSsoBridge.ts';

export type AuthStatus = 'loading' | 'anon' | 'signed-in';

export interface UserProfile {
  tier: Tier;
  role: Role;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
}

interface UserState {
  status: AuthStatus;
  userId: string | null;
  email: string | null;
  profile: UserProfile | null;

  /** Bootstrap on app mount (and after auth-callback). Idempotent. */
  refresh: () => Promise<void>;
  /** Open Supabase OAuth flow. provider defaults to 'google'. */
  signIn: (
    provider?: 'google' | 'github' | 'apple',
    options?: { readonly skipSso?: boolean },
  ) => Promise<void>;
  /** Sign out and clear local profile cache. */
  signOut: () => Promise<void>;

  /** Selector: viewer's effective tier (for client gate predicates). */
  effectiveTier: () => Tier;
}

const STORE_VERSION = 1;

interface MeResponse {
  session: { userId: string; email: string | null } | null;
  profile: {
    tier: string;
    role: string;
    displayName: string | null;
    avatarUrl: string | null;
    username: string | null;
  } | null;
  effectiveTier: string;
}

/**
 * Pure-fn — best-effort detection that an unknown thrown error from
 * the supabase-js client is a refresh-token-not-found failure. The
 * SDK throws an `AuthApiError` whose `.message` includes "Invalid
 * Refresh Token" or "Refresh Token Not Found"; we also check the
 * status (400 from gotrue) and the AuthApiError class name as defence
 * against future SDK message-string churn.
 *
 * Exported for unit testing.
 */
export function isRefreshTokenInvalid(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const obj = err as { name?: unknown; message?: unknown; status?: unknown; code?: unknown };
  const msg = typeof obj.message === 'string' ? obj.message.toLowerCase() : '';
  if (msg.includes('refresh token') && (msg.includes('not found') || msg.includes('invalid'))) {
    return true;
  }
  const code = typeof obj.code === 'string' ? obj.code : '';
  if (code === 'refresh_token_not_found') return true;
  return false;
}

async function fetchMe(): Promise<MeResponse | null> {
  try {
    const session = await supabase.auth.getSession();
    const accessToken = session.data.session?.access_token;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    const res = await fetch('/api/auth/me', { headers, cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as MeResponse;
  } catch (err: unknown) {
    // Refresh-token-expired is a recoverable failure — Supabase rotated
    // the token (typical for users who haven't visited in >60 days) but
    // the client still cached the old one. Clear local session so the
    // user lands on Welcome instead of a stuck "loading" screen.
    if (isRefreshTokenInvalid(err)) {
      try {
        await supabase.auth.signOut();
      } catch {
        /* signOut from broken session can also throw — best effort */
      }
    }
    return null;
  }
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      status: isAuthConfigured ? 'loading' : 'anon',
      userId: null,
      email: null,
      profile: null,

      refresh: async () => {
        if (!isAuthConfigured) {
          set({ status: 'anon', userId: null, email: null, profile: null });
          return;
        }
        const me = await fetchMe();
        if (!me || !me.session) {
          set({ status: 'anon', userId: null, email: null, profile: null });
          return;
        }
        const profile: UserProfile | null = me.profile
          ? {
              tier: coerceTier(me.profile.tier),
              role: coerceRole(me.profile.role),
              username: me.profile.username,
              displayName: me.profile.displayName,
              avatarUrl: me.profile.avatarUrl,
            }
          : null;
        set({
          status: 'signed-in',
          userId: me.session.userId,
          email: me.session.email,
          profile,
        });
      },

      signIn: async (provider = 'google', options) => {
        if (!isAuthConfigured) return;

        // ADR-0099/0100 + ADR-0330: cross-eTLD spokes (gghere.com, jubuddy.com)
        // can't share the `*.jubit.ai` Supabase cookie. On a non-jubit.ai host
        // the popup-bridge imports a Supabase session so Planet spend does
        // not require starting on jubit.ai. Kill switch: VITE_JUBIT_USE_SSO_BRIDGE=false.
        // skipSso: the UI already tried the popup and wants OAuth only.
        if (!options?.skipSso && shouldUseSsoBridge()) {
          try {
            const { accessToken, refreshToken } = await openJubitSsoBridge();
            // S3: clear sticky local session so a successful handoff cannot be
            // shadowed by a prior jubuddy LS identity (report RC-3 / S3).
            try {
              await supabase.auth.signOut({ scope: 'local' });
            } catch {
              /* best-effort — setSession still attempted below */
            }
            // S2: mirror jubitSsoExchange — check setSession `{ error }`.
            // A failed replace must surface; never silent Google OAuth fallthrough
            // (sticky wrong account). Cancel / popup-blocked keep prior behaviour.
            const { error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (setSessionError) {
              throw new JubitSsoBridgeError(
                'set-session-failed',
                setSessionError.message || 'Failed to apply SSO session',
              );
            }
            await get().refresh();
            return;
          } catch (err) {
            // Cancellation is expected (user closed popup) — silent return,
            // matches Supabase OAuth behaviour where a navigated-away user
            // simply doesn't sign in.
            if (err instanceof JubitSsoBridgeError && err.code === 'cancelled') return;
            // S+1: after a bridge attempt, never auto-OAuth. Surface every
            // other miss (malformed / timeout / state-mismatch / set-session-
            // failed / popup-blocked / bridge-not-configured / unknown).
            // OAuth only when skipSso: true (continueWithOAuth) or bridge off.
            throw err;
          }
        }

        // Stash the return path locally so AuthCallback can recover even if
        // Supabase's "Site URL" override strips our `?next=` param. Sprint 28+:
        // observed that `redirectTo` is silently ignored when the host isn't in
        // the project-level Redirect URLs allowlist, so users land at jubit.ai
        // instead of jubuddy.com / gghere.com. The localStorage breadcrumb lets
        // them recover via the gate's "I already signed in" button.
        // Include the hash: these surfaces are deep-linked by section
        // (/planet#hk, /football-academy#venues), and dropping the fragment returns
        // the visitor to the top of the page they were already inside — which reads
        // as the sign-in having thrown away their place.
        // `|| ''` because a partial `location` (jsdom stubs, embedded webviews) leaves
        // hash undefined, which would otherwise append the literal string "undefined"
        // to the return path.
        const returnTo =
          window.location.pathname + window.location.search + (window.location.hash || '');
        try {
          window.localStorage.setItem('jubuddy:auth-return-to', returnTo);
        } catch {
          /* private mode / cookies disabled — best effort */
        }
        const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(
          returnTo,
        )}`;
        await supabase.auth.signInWithOAuth({ provider, options: { redirectTo } });
      },

      signOut: async () => {
        if (isAuthConfigured) {
          try {
            await supabase.auth.signOut();
          } catch {
            // Ignore — local state still cleared below.
          }
        }
        set({ status: 'anon', userId: null, email: null, profile: null });
      },

      effectiveTier: () => getEffectiveTier(get().profile),
    }),
    {
      name: `jubuddy:user-store:v${STORE_VERSION}`,
      // Only persist the profile snapshot. Session lives in supabase storage;
      // status is recomputed on every mount via refresh().
      partialize: (s) => ({
        userId: s.userId,
        email: s.email,
        profile: s.profile,
      }),
    },
  ),
);

/**
 * Drop any per-user data caches that should not survive a sign-in/out.
 * The ThemeFactory roster cache, and the planet's signed skin URLs; add others here as needed.
 */
function clearPerUserCaches(): void {
  // Dynamic so the planet chunk is never pinned into the main entry (rule 050). Fire and
  // forget: if the chunk cannot load there is nothing cached from it to clear either.
  void import('../planet/data/userSkins.ts')
    .then((m) => m.clearUserSkinCache())
    .catch(() => undefined);
  void import('../planet/data/userBuildings.ts')
    .then((m) => m.clearUserBuildingCache())
    .catch(() => undefined);
  void import('../planet/data/userRooms.ts')
    .then((m) => m.clearUserRoomCache())
    .catch(() => undefined);
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem('jubuddy:theme-roster:v10');
    sessionStorage.removeItem('jubuddy:theme-roster:v10:adult');
  } catch {
    /* non-fatal */
  }
}

/**
 * One-time auth-state subscription. Wired in App.tsx so any sign-in/out
 * triggered elsewhere in the tab (Supabase popup, multi-tab) refreshes
 * the store automatically. No-op when auth is not configured.
 */
export function subscribeToAuthChanges(): () => void {
  if (!isAuthConfigured) return () => undefined;
  const { data } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      clearPerUserCaches();
      useUserStore.setState({ status: 'anon', userId: null, email: null, profile: null });
      return;
    }
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
      clearPerUserCaches();
      void useUserStore.getState().refresh();
    }
  });
  // GoTrueClient surfaces refresh-token-expired as an unhandled rejection
  // outside the onAuthStateChange callback (background timer fires the
  // refresh). Catch it here so the console doesn't fill with
  // `AuthApiError: Invalid Refresh Token: Refresh Token Not Found`. The
  // SDK will emit SIGNED_OUT shortly after, which clears local state via
  // the handler above.
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      if (isRefreshTokenInvalid(event.reason)) {
        event.preventDefault();
        // Force-clear local session so subsequent /api/auth/me calls do
        // not retry with the dead refresh token.
        void supabase.auth.signOut().catch(() => undefined);
      }
    });
  }
  return () => data.subscription.unsubscribe();
}
